import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { animate } from "motion";
import { useAuth } from "../../contexts/AuthProvider";
import { usePmProjectMembership } from "../../hooks/usePmProjectMembership";
import { resolveDashboardTier } from "../../pages/AuditDashboard/dashboardRoleTiers";
import { isSettingsModule } from "../../config/settingsModules";
import {
  LayoutDashboard,
  LayoutGrid,
  Users,
  FolderOpen,
  ReceiptText,
  ClipboardClock,
  BarChart3,
  ClipboardList,
  BriefcaseBusiness,
  ListChecks,
  Calendar,
  BookOpen,
  Database,
  Package,
  GraduationCap,
  ChevronDown,
  Lock,
  FileText,
  Settings,
  Wrench,
  ShieldCheck,
  Mail,
  KanbanSquare,
  Ticket,
  Activity,
  FileText as FileTemplate,
  Building2,
  UserSquare,
  Banknote,
  UserPlus,
  Clock,
  Percent,
  Award,
  CalendarDays,
  Target,
  UserCircle,
  PieChart,
  Archive,
} from "lucide-react";

const NavLinks = ({ isCollapsed, forceCloseDropdowns }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const currentSearch = location.search;
  const { hasRole, can, user } = useAuth();

  const isAdmin = hasRole('ROLE_ADMIN');
  const isDirector = hasRole('ROLE_DIRECTOR');
  const isFinance = hasRole('ROLE_FINANCE');
  const isOperationManager = hasRole('ROLE_OPERATION_MANAGER');
  const canSeeAuditorUtilization = isOperationManager || resolveDashboardTier(user) === 'management';

  // ROLE_DIRECTOR's blanket bypass deliberately stops at the Settings section:
  // those modules configure the system itself, and a bypass on set-permission
  // would let a Director grant themselves every other module. See
  // config/settingsModules.js; ModuleRoute applies the same rule to the routes.
  const canViewModule = (moduleName) =>
    isAdmin ||
    (isDirector && !isSettingsModule(moduleName)) ||
    can('view', moduleName) ||
    can('view_own', moduleName) ||
    can('view_department', moduleName) ||
    can('view_global', moduleName);

  const canViewAchievementForm = canViewModule('achievement');

  const isPathInCategory = (pathsArray) =>
    pathsArray.some(path => {
      if (path === '/clients') return currentPath.startsWith('/clients/');
      return currentPath === path;
    });

  const isAuditTaskRoute = currentPath === '/task' && new URLSearchParams(currentSearch).get('tab') === 'audit-task';
  const isTaskRoute = (currentPath === '/task' || currentPath.startsWith('/task/')) && !isAuditTaskRoute;

  // Path groups
  const crmPaths = [
    '/crm-dashboard', '/company', '/leads', '/proposals', '/clients',
    '/project', '/projects', '/expense', '/invoice', '/payment',
    '/task', '/reports/company', '/reports/leads',
    '/reports/proposals', '/reports/projects', '/analysis', '/reports/sales-report',
    '/reports/achievement-form'
  ];
  const crmProspectPaths = ['/company', '/leads', '/proposals'];
  const crmSalesPaths = ['/invoice', '/payment'];
  const crmReportsPaths = ['/reports/company', '/reports/leads', '/reports/proposals', '/reports/projects', '/analysis'];
  const auditPlatformPaths = [
    '/audit-dashboard',
    '/project-monitoring',
    '/audit-reconciliation',
    '/audit/clients',
    '/audit/client-matrix',
    '/audit/sales-client-dashboard',
    '/audit-output-template',
    '/audit/form-catalog',
    '/audit/form-entry',
    '/audit-libraries',
    '/calendar',
    '/audit/certificate-monitoring',
    '/audit/competency-management',
    '/audit/finance-confirmation',
    '/tools/load-balancer',
    '/tools/auditor-utilization',
  ];
  const auditLibrariesPaths = ['/audit-libraries/v1', '/audit-libraries/v2'];

  const isCrmActive = () =>
    currentPath === '/crm-dashboard' ||
    isPathInCategory(crmProspectPaths) ||
    currentPath === '/clients' || currentPath.startsWith('/clients/') ||
    currentPath === '/project' || currentPath.startsWith('/projects/') ||
    currentPath === '/expense' ||
    isPathInCategory(crmSalesPaths) ||
    isTaskRoute ||
    isPathInCategory(crmReportsPaths) ||
    currentPath === '/reports/sales-report';

  const isAuditActive = () =>
    currentPath === '/audit-dashboard' ||
    currentPath === '/project-monitoring' ||
    currentPath === '/audit/clients' ||
    currentPath.startsWith('/audit/clients/') ||
    currentPath === '/audit/client-matrix' ||
    currentPath === '/audit/sales-client-dashboard' ||
    currentPath === '/audit-output-template' ||
    currentPath === '/audit/form-catalog' ||
        currentPath === '/audit/form-entry' ||
    currentPath === '/audit/certificate-monitoring' ||
    currentPath === '/audit/competency-management' ||
    currentPath === '/audit/finance-confirmation' ||
    currentPath === '/audit-reconciliation' ||
    currentPath === '/tools/load-balancer' ||
    currentPath === '/tools/auditor-utilization' ||
    isAuditTaskRoute ||
    currentPath === '/calendar' ||
    currentPath.startsWith('/audit-libraries');

  const isProjectManagementActive = () =>
    currentPath === '/project-management' ||
    currentPath.startsWith('/project-management/') ||
    currentPath.startsWith('/pm/');

  const isProjectManagementLibraryActive = () =>
    currentPath === '/project-management' ||
    currentPath.startsWith('/project-management/');

  const settingsPaths = [
    '/create-template', '/approval-workflow', '/rate-management', '/create-type-unit',
    '/proposal-stage', '/master-currency', '/format-number', '/expense-category', '/chart-of-account',
    '/sales-target', '/create-role', '/set-permission', '/email-template', '/email-configuration',
  ];
  const settingsToolsPaths = [
    '/create-template', '/approval-workflow', '/rate-management', '/create-type-unit',
    '/proposal-stage', '/master-currency', '/format-number', '/expense-category', '/chart-of-account',
    '/sales-target',
  ];
  const settingsRolePaths = ['/create-role', '/set-permission'];
  const settingsEmailPaths = ['/email-template', '/email-configuration'];
  
  const isSettingsActive = () => isPathInCategory(settingsPaths);

  const hrisPaths = [
    "/hris/dashboard",
    "/hris/recruitment",
    "/hris/recruitment/manpower-request",
    "/hris/recruitment/job-requisition",
    "/hris/recruitment/job-vacancies",
    "/hris/recruitment/candidates",
    "/hris/recruitment/selection-process",
    "/hris/recruitment/job-offering",
    "/hris/recruitment/hiring-approval",
    "/hris/recruitment/pre-onboarding",
    "/hris/recruitment/reports",
    "/hris/performance",
    "/hris/employees",
    "/hris/department",
    "/hris/job-title",
    "/hris/branch",
    "/hris/grades",
    "/hris/organization-structure",
    "/hris/competencies",
    "/hris/employee-assignments",
    "/hris/employment-administration",
    "/hris/employee-documents",
    "/hris/employee-reports",
  ];
  const hrisRecruitmentPaths = [
    "/hris/recruitment",
    "/hris/recruitment/manpower-request",
    "/hris/recruitment/job-requisition",
    "/hris/recruitment/job-vacancies",
    "/hris/recruitment/candidates",
    "/hris/recruitment/selection-process",
    "/hris/recruitment/job-offering",
    "/hris/recruitment/hiring-approval",
    "/hris/recruitment/pre-onboarding",
    "/hris/recruitment/reports",
  ];
  const hrisEmployeePaths = [
    "/hris/dashboard",
    "/hris/employees",
    "/hris/department",
    "/hris/job-title",
    "/hris/branch",
    "/hris/grades",
    "/hris/organization-structure",
    "/hris/competencies",
    "/hris/employee-assignments",
    "/hris/employment-administration",
    "/hris/employee-documents",
    "/hris/employee-reports",
  ];
  const hrisOrganizationPaths = [
    "/hris/department",
    "/hris/job-title",
    "/hris/grades",
    "/hris/organization-structure",
  ];
  const hrisPerformancePaths = ["/hris/performance"];
  const isHrisActive = () =>
    isPathInCategory(hrisPaths) || currentPath.startsWith("/hris/employees/");

  const [openDropdowns, setOpenDropdowns] = useState({
    crm: false,
    crmProspect: false,
    crmSales: false,
    crmReports: false,
    auditPlatform: false,
    auditLibraries: false,
    projectManagement: false,
    projectManagementLibrary: false,
    hris: false,
    hrisRecruitment: false,
    hrisEmployee: false,
    hrisOrganization: false,
    hrisPerformance: false,
    settings: false,
    settingsTools: false,
    settingsRole: false,
    settingsEmail: false,
  });

  useEffect(() => {
    if (forceCloseDropdowns) {
      setOpenDropdowns({
        crm: false,
        crmProspect: false,
        crmSales: false,
        crmReports: false,
        auditPlatform: false,
        auditLibraries: false,
        projectManagement: false,
        projectManagementLibrary: false,
        hris: false,
        hrisRecruitment: false,
        hrisEmployee: false,
        hrisOrganization: false,
        hrisPerformance: false,
        settings: false,
        settingsTools: false,
        settingsRole: false,
        settingsEmail: false,
      });
    }
  }, [forceCloseDropdowns]);

  useEffect(() => {
    if (!forceCloseDropdowns) {
      setOpenDropdowns({
        crm: isCrmActive(),
        crmProspect: isPathInCategory(crmProspectPaths),
        crmSales: isPathInCategory(crmSalesPaths),
        crmReports: isPathInCategory(crmReportsPaths),
        auditPlatform: isAuditActive(),
        auditLibraries: currentPath.startsWith('/audit-libraries'),
        projectManagement: isProjectManagementActive(),
        projectManagementLibrary: isProjectManagementLibraryActive(),
        settings: isSettingsActive(),
        hris: isHrisActive(),
        hrisRecruitment: isPathInCategory(hrisRecruitmentPaths),
        hrisEmployee:
          isPathInCategory(hrisEmployeePaths) ||
          currentPath.startsWith("/hris/employees/"),
        hrisOrganization: isPathInCategory(hrisOrganizationPaths),
        hrisPerformance: isPathInCategory(hrisPerformancePaths),
        settingsTools: isPathInCategory(settingsToolsPaths),
        settingsRole: isPathInCategory(settingsRolePaths),
        settingsEmail: isPathInCategory(settingsEmailPaths),
      });
    }
  }, [currentPath, currentSearch, forceCloseDropdowns]);

  const toggleDropdown = (dropdown, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!isCollapsed) {
      setOpenDropdowns(prev => ({
        ...prev,
        [dropdown]: !prev[dropdown]
      }));
    }
  };

  const animateDropdown = (element, isOpen) => {
    if (element) {
      if (isOpen && !isCollapsed) {
        element.style.display = 'block';
        animate(element, { opacity: 1, height: 'auto' }, { duration: 0.2, easing: "ease-out" });
      } else {
        animate(element, { opacity: 0, height: 0 }, {
          duration: 0.2,
          easing: "ease-in",
          onComplete: () => { element.style.display = 'none'; }
        });
      }
    }
  };

  const navClasses = `navbar-nav mx-2 pt-lg-3 ${isCollapsed ? 'navbar-collapsed' : ''}`;

  const ComingSoonBadge = () => (
    <span className="badge bg-secondary ms-auto" style={{ fontSize: '0.6rem', opacity: 0.7 }}>Soon</span>
  );

  // ── CRM sub-menus visibility
  const canSeeCrmProspect = canViewModule('prospect') || canViewModule('leads') || canViewModule('proposal');
  const canSeeCrmSales = canViewModule('invoice') || canViewModule('payment');
  const canSeeCrmReports = canViewModule('prospect') || canViewModule('leads') || canViewModule('proposal') || canViewModule('project') || canViewModule('analysis');
  const canSeeCrm = canSeeCrmProspect || canViewModule('client') || canViewModule('project') ||
    canViewModule('expense') || canSeeCrmSales || canViewModule('task') || canViewModule('calendar') ||
    canSeeCrmReports || canViewModule('sales-report') || canViewModule('crm-dashboard');
  const canViewAuditProcess = canViewModule('audit') || canViewModule('task');
  const canSeeSalesAuditDashboard = hasRole('ROLE_SALES');
  const canSeeAudit = isAdmin || isDirector || isFinance || canViewAuditProcess || canViewModule('audit-output-template') || canViewModule('calendar') || canViewModule('audit-dashboard') || canViewModule('project') || canSeeSalesAuditDashboard;
  // Settings visibility is derived from its children rather than hard-coded to
  // ROLE_ADMIN, so a role granted e.g. only 'rate-management' sees a Settings menu
  // containing exactly that one entry instead of an empty dropdown.
  const settingsToolsModules = [
    'create-template', 'workflow', 'rate-management', 'create-type-unit',
    'proposal-stage', 'format-number', 'expense-category', 'chart-of-account',
    'master-currency', 'sales-target',
  ];
  const settingsRoleModules = ['create-role', 'set-permission'];
  const settingsEmailModules = ['email-template', 'email-configuration'];

  const canSeeSettingsTools = settingsToolsModules.some((m) => canViewModule(m));
  const canSeeSettingsRole = settingsRoleModules.some((m) => canViewModule(m));
  const canSeeSettingsEmail = settingsEmailModules.some((m) => canViewModule(m));
  const canSeeSettings = canSeeSettingsTools || canSeeSettingsRole || canSeeSettingsEmail;
  // isPmProjectMember: a user added to any project's team (via the Go PM
  // backend's per-project membership, independent of role-based Set
  // Permission grants) also gets the Projects menu, regardless of role.
  // Scoped to just pm-projects (not dashboard/timesheet/library) since
  // that's what project-team membership actually concerns — see
  // usePmProjectMembership.js.
  const { isPmProjectMember } = usePmProjectMembership();
  const canViewPmDashboard = canViewModule('pm-dashboard');
  const canViewPmProjects = canViewModule('pm-projects') || isPmProjectMember;
  const canViewPmTimesheet = canViewModule('pm-timesheet');
  const canViewPmLibrary = canViewModule('pm-library');
  const canSeeProjectManagement =
    canViewPmDashboard || canViewPmProjects || canViewPmTimesheet || canViewPmLibrary;
  const pmTabFromUrl = new URLSearchParams(currentSearch).get('tab');
  const isPmTab = (key) => isProjectManagementLibraryActive() && (pmTabFromUrl || 'dashboard') === key;
  const canSeeHris =
    canViewModule("hris") ||
    canViewModule("employee") ||
    canViewModule("department") ||
    canViewModule("recruitment") ||
    canViewModule("performance");

  const DropdownToggleItem = ({ dropdownKey, icon: Icon, label, active }) => (
    <button
      type="button"
      className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center border-0 ${
        active ? "active" : ""
      }`}
      onClick={(e) => toggleDropdown(dropdownKey, e)}
    >
      <Icon size={14} className="me-2 opacity-60" />
      <span className="flex-grow-1">{label}</span>
      <ChevronDown
        size={12}
        style={{
          transform: openDropdowns[dropdownKey] ? "rotate(180deg)" : "none",
          transition: "transform 0.2s",
        }}
      />
    </button>
  );

  const ComingSoonItem = ({ icon: Icon, label }) => (
    <span
      className="dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center text-muted"
      style={{ cursor: "default", opacity: 0.68 }}
    >
      <Icon size={14} className="me-2 opacity-60" />
      <span className="flex-grow-1">{label}</span>
      <ComingSoonBadge />
    </span>
  );

  return (
    <ul className={navClasses}>
      {/* ── MAIN DASHBOARD ── */}
      <li className="nav-item mb-1">
        <Link
          className={`nav-link rounded-3 ${currentPath === '/dashboard' || currentPath === '/' ? 'active' : ''}`}
          to="/dashboard"
        >
          <span className="nav-link-icon d-md-none d-lg-inline-block">
            <LayoutDashboard className="icon icon-tabler" />
          </span>
          <span className="nav-link-title">Dashboard</span>
        </Link>
      </li>

      {/* ── CRM ── */}
      {canSeeCrm && (
        <li className={`nav-item mb-1 dropdown ${openDropdowns.crm && !isCollapsed ? 'show' : ''}`}>
          <a
            className={`nav-link dropdown-toggle rounded-3 ${isCrmActive() ? 'active' : ''}`} 
            href="#"
            onClick={(e) => toggleDropdown("crm", e)}
            role="button"
            aria-expanded={openDropdowns.crm && !isCollapsed}
          >
            <span className="nav-link-icon d-md-none d-lg-inline-block">
              <BriefcaseBusiness className="icon icon-tabler" />
            </span>
            <span className="nav-link-title">CRM</span>
          </a>
          <div
            className={`dropdown-menu ${openDropdowns.crm && !isCollapsed ? "show" : ""}`}
            ref={(el) =>
              el && animateDropdown(el, openDropdowns.crm && !isCollapsed)
            }
          >
            {/* CRM Dashboard */}
            {canViewModule("crm-dashboard") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/crm-dashboard" ? "active" : ""}`}
                to="/crm-dashboard"
              >
                <LayoutDashboard size={14} className="me-2 opacity-60" />{" "}
                Dashboard
              </Link>
            )}

            {/* Prospect sub-dropdown */}
            {canSeeCrmProspect && (
              <>
                <a
                  className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${isPathInCategory(crmProspectPaths) ? "active" : ""}`}
                  href="#"
                  onClick={(e) => toggleDropdown("crmProspect", e)}
                >
                  <Users size={14} className="me-2 opacity-60" />
                  <span className="flex-grow-1">Prospect</span>
                  <ChevronDown
                    size={12}
                    style={{
                      transform: openDropdowns.crmProspect
                        ? "rotate(180deg)"
                        : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </a>
                <div
                  className={`ps-3 ${openDropdowns.crmProspect && !isCollapsed ? "" : "d-none"}`}
                  ref={(el) =>
                    el &&
                    animateDropdown(
                      el,
                      openDropdowns.crmProspect && !isCollapsed,
                    )
                  }
                >
                  {canViewModule("prospect") && (
                    <Link
                      className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/company" ? "active" : ""}`}
                      to="/company"
                    >
                      Company
                    </Link>
                  )}
                  {canViewModule("leads") && (
                    <Link
                      className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/leads" ? "active" : ""}`}
                      to="/leads"
                    >
                      Leads
                    </Link>
                  )}
                  {canViewModule("proposal") && (
                    <Link
                      className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/proposals" || currentPath.startsWith("/proposals/") ? "active" : ""}`}
                      to="/proposals"
                    >
                      Proposals
                    </Link>
                  )}
                </div>
              </>
            )}

            {canViewModule("client") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/clients" || currentPath.startsWith("/clients/") ? "active" : ""}`}
                to="/clients"
              >
                <Building2 size={14} className="me-2 opacity-60" /> Clients
              </Link>
            )}

            {/* Project */}
            {canViewModule("project") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/project" || currentPath.startsWith("/projects/") ? "active" : ""}`}
                to="/project"
              >
                <FolderOpen size={14} className="me-2 opacity-60" /> Project
              </Link>
            )}

            {/* Expense */}
            {canViewModule("expense") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/expense" ? "active" : ""}`}
                to="/expense"
              >
                <ReceiptText size={14} className="me-2 opacity-60" /> Expense
              </Link>
            )}

            {/* Sales sub-dropdown */}
            {canSeeCrmSales && (
              <>
                <a
                  className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${isPathInCategory(crmSalesPaths) ? "active" : ""}`}
                  href="#"
                  onClick={(e) => toggleDropdown("crmSales", e)}
                >
                  <ReceiptText size={14} className="me-2 opacity-60" />
                  <span className="flex-grow-1">Sales</span>
                  <ChevronDown
                    size={12}
                    style={{
                      transform: openDropdowns.crmSales
                        ? "rotate(180deg)"
                        : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </a>
                <div
                  className={`ps-3 ${openDropdowns.crmSales && !isCollapsed ? "" : "d-none"}`}
                  ref={(el) =>
                    el &&
                    animateDropdown(el, openDropdowns.crmSales && !isCollapsed)
                  }
                >
                  {canViewModule("invoice") && (
                    <Link
                      className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/invoice" ? "active" : ""}`}
                      to="/invoice"
                    >
                      Invoice
                    </Link>
                  )}
                  {canViewModule("payment") && (
                    <Link
                      className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/payment" ? "active" : ""}`}
                      to="/payment"
                    >
                      Payment
                    </Link>
                  )}
                </div>
              </>
            )}

            {/* Task */}
            {canViewModule("task") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${isTaskRoute ? "active" : ""}`}
                to="/task"
              >
                <ListChecks size={14} className="me-2 opacity-60" /> Task
              </Link>
            )}

            {/* Reports sub-dropdown */}
            {canSeeCrmReports && (
              <>
                <a
                  className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${isPathInCategory(crmReportsPaths) ? "active" : ""}`}
                  href="#"
                  onClick={(e) => toggleDropdown("crmReports", e)}
                >
                  <ClipboardClock size={14} className="me-2 opacity-60" />
                  <span className="flex-grow-1">Reports</span>
                  <ChevronDown
                    size={12}
                    style={{
                      transform: openDropdowns.crmReports
                        ? "rotate(180deg)"
                        : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </a>
                <div
                  className={`ps-3 ${openDropdowns.crmReports && !isCollapsed ? "" : "d-none"}`}
                  ref={(el) =>
                    el &&
                    animateDropdown(
                      el,
                      openDropdowns.crmReports && !isCollapsed,
                    )
                  }
                >
                  {canViewModule("analysis") && (
                    <Link
                      className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/analysis" || currentPath.startsWith("/analysis/") ? "active" : ""}`}
                      to="/analysis"
                    >
                      Analysis
                    </Link>
                  )}
                  {canViewModule("prospect") && (
                    <Link
                      className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/reports/company" ? "active" : ""}`}
                      to="/reports/company"
                    >
                      Company
                    </Link>
                  )}
                  {canViewModule("leads") && (
                    <Link
                      className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/reports/leads" ? "active" : ""}`}
                      to="/reports/leads"
                    >
                      Leads
                    </Link>
                  )}
                  {canViewModule("proposal") && (
                    <Link
                      className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/reports/proposals" ? "active" : ""}`}
                      to="/reports/proposals"
                    >
                      Proposals
                    </Link>
                  )}
                  {canViewModule("project") && (
                    <Link
                      className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/reports/projects" ? "active" : ""}`}
                      to="/reports/projects"
                    >
                      Projects
                    </Link>
                  )}
                </div>
              </>
            )}

            {/* Sales Report */}
            {canViewModule("sales-report") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/reports/sales-report" ? "active" : ""}`}
                to="/reports/sales-report"
              >
                <BarChart3 size={14} className="me-2 opacity-60" /> Sales Report
              </Link>
            )}

            {/* Achievement & Incentive Form — gated by the "achievement" module's permissions (Set Permissions) */}
            {canViewAchievementForm && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === '/reports/achievement-form' ? 'active' : ''}`}
                to="/reports/achievement-form"
              >
                <BarChart3 size={14} className="me-2 opacity-60" /> Achievement & Incentive
              </Link>
            )}

          </div>
        </li>
      )}

      {/* ── AUDIT PLATFORM ── */}
      {canSeeAudit && (
        <li
          className={`nav-item mb-1 dropdown ${openDropdowns.auditPlatform && !isCollapsed ? "show" : ""}`}
        >
          <a
            className={`nav-link dropdown-toggle rounded-3 ${isAuditActive() ? "active" : ""}`}
            href="#"
            onClick={(e) => toggleDropdown("auditPlatform", e)}
            role="button"
            aria-expanded={openDropdowns.auditPlatform && !isCollapsed}
          >
            <span className="nav-link-icon d-md-none d-lg-inline-block">
              <ClipboardList className="icon icon-tabler" />
            </span>
            <span className="nav-link-title">Audit Platform</span>
          </a>
          <div
            className={`dropdown-menu ${openDropdowns.auditPlatform && !isCollapsed ? "show" : ""}`}
            ref={(el) =>
              el &&
              animateDropdown(el, openDropdowns.auditPlatform && !isCollapsed)
            }
          >
            {/* Audit Dashboard */}
            {(canViewModule("audit-dashboard") || canSeeSalesAuditDashboard) && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/audit-dashboard" ? "active" : ""}`}
                to="/audit-dashboard"
              >
                <LayoutDashboard size={14} className="me-2 opacity-60" />{" "}
                Dashboard
              </Link>
            )}

            {canViewModule("project") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/project-monitoring" ? "active" : ""}`}
                to="/project-monitoring"
              >
                <FolderOpen size={14} className="me-2 opacity-60" /> Project Monitoring
              </Link>
            )}

            {canViewModule("audit") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/audit/clients" || currentPath.startsWith("/audit/clients/") ? "active" : ""}`}
                to="/audit/clients"
              >
                <Building2 size={14} className="me-2 opacity-60" /> Clients
              </Link>
            )}

            {canViewModule("audit") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/audit/client-matrix" ? "active" : ""}`}
                to="/audit/client-matrix"
              >
                <LayoutGrid size={14} className="me-2 opacity-60" /> Client Matrix
              </Link>
            )}

            {canViewModule("audit") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/audit/sales-client-dashboard" ? "active" : ""}`}
                to="/audit/sales-client-dashboard"
              >
                <LayoutDashboard size={14} className="me-2 opacity-60" /> Sales Client Dashboard
              </Link>
            )}

            {/* Audit Process */}
            {canViewAuditProcess && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${isAuditTaskRoute ? "active" : ""}`}
                to="/task?tab=audit-task"
              >
                <ListChecks size={14} className="me-2 opacity-60" /> Audit Task
              </Link>
            )}

            {/* Calendar */}
            {canViewModule("calendar") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/calendar" ? "active" : ""}`}
                to="/calendar"
              >
                <Calendar size={14} className="me-2 opacity-60" /> Calendar
              </Link>
            )}

            {/* Output Audit Template */}
            <Link
              className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/audit-output-template" ? "active" : ""}`}
              to="/audit-output-template"
            >
              <FileText size={14} className="me-2 opacity-60" /> Output Audit
              Template
            </Link>

            {canViewModule("audit") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/audit/certificate-monitoring" ? "active" : ""}`}
                to="/audit/certificate-monitoring"
              >
                <FileText size={14} className="me-2 opacity-60" /> Certificate
                Monitoring
              </Link>
            )}

            {canViewModule("audit") && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${currentPath === "/audit/competency-management" ? "active" : ""}`}
                to="/audit/competency-management"
              >
                <Award size={14} className="me-2 opacity-60 flex-shrink-0" />
                <span>Competency Management</span>
              </Link>
            )}

            {(isAdmin || isDirector || isFinance) && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/audit/finance-confirmation" ? "active" : ""}`}
                to="/audit/finance-confirmation"
              >
                <ReceiptText size={14} className="me-2 opacity-60" /> Finance
                Confirmation
              </Link>
            )}

            {canSeeAuditorUtilization && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/tools/auditor-utilization" ? "active" : ""}`}
                to="/tools/auditor-utilization"
              >
                <Percent size={14} className="me-2 opacity-60" /> Auditor
                Utilization
              </Link>
            )}

            {/* Audit Libraries sub-dropdown */}
            <a
              className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${currentPath.startsWith("/audit-libraries") ? "active" : ""}`}
              href="#"
              onClick={(e) => toggleDropdown("auditLibraries", e)}
            >
              <BookOpen size={14} className="me-2 opacity-60" />
              <span className="flex-grow-1">Audit Libraries</span>
              <ChevronDown
                size={12}
                style={{
                  transform: openDropdowns.auditLibraries
                    ? "rotate(180deg)"
                    : "none",
                  transition: "transform 0.2s",
                }}
              />
            </a>
            <div
              className={`ps-3 ${openDropdowns.auditLibraries && !isCollapsed ? "" : "d-none"}`}
              ref={(el) =>
                el &&
                animateDropdown(
                  el,
                  openDropdowns.auditLibraries && !isCollapsed,
                )
              }
            >
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/audit-libraries/v1" ? "active" : ""}`}
                to="/audit-libraries/v1"
              >
                v1
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/audit-libraries/v2" ? "active" : ""}`}
                to="/audit-libraries/v2"
              >
                v2
              </Link>
            </div>
          </div>
        </li>
      )}

      {/* ── PROJECT MANAGEMENT (admin-only) ── */}
      {canSeeProjectManagement && (
        <li
          className={`nav-item mb-1 dropdown ${openDropdowns.projectManagement && !isCollapsed ? "show" : ""}`}
        >
          <a
            className={`nav-link dropdown-toggle rounded-3 ${isProjectManagementActive() ? "active" : ""}`}
            href="#"
            onClick={(e) => toggleDropdown("projectManagement", e)}
            role="button"
            aria-expanded={openDropdowns.projectManagement && !isCollapsed}
          >
            <span className="nav-link-icon d-md-none d-lg-inline-block">
              <FolderOpen className="icon icon-tabler" />
            </span>
            <span className="nav-link-title">Project Management</span>
          </a>
          <div
            className={`dropdown-menu ${openDropdowns.projectManagement && !isCollapsed ? "show" : ""}`}
            ref={(el) =>
              el &&
              animateDropdown(
                el,
                openDropdowns.projectManagement && !isCollapsed,
              )
            }
          >
            {canViewPmDashboard && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/pm/dashboard" ? "active" : ""}`}
                to="/pm/dashboard"
              >
                <LayoutDashboard size={14} className="me-2 opacity-60" />{" "}
                Dashboard
              </Link>
            )}
            {canViewPmProjects && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/pm/projects" ? "active" : ""}`}
                to="/pm/projects"
              >
                <BriefcaseBusiness size={14} className="me-2 opacity-60" />{" "}
                Projects
              </Link>
            )}
            {canViewPmTimesheet && (
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/pm/timesheet" ? "active" : ""}`}
                to="/pm/timesheet"
              >
                <Clock size={14} className="me-2 opacity-60" /> Timesheet
              </Link>
            )}
            {canViewPmLibrary && (
              <>
                <a
                  className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${isProjectManagementLibraryActive() ? "active" : ""}`}
                  href="#"
                  onClick={(e) => toggleDropdown("projectManagementLibrary", e)}
                >
                  <Archive size={14} className="me-2 opacity-60" />
                  <span className="flex-grow-1">Library</span>
                  <ChevronDown
                    size={12}
                    style={{
                      transform: openDropdowns.projectManagementLibrary
                        ? "rotate(180deg)"
                        : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </a>
                <div
                  className={`ps-3 ${openDropdowns.projectManagementLibrary && !isCollapsed ? "" : "d-none"}`}
                  ref={(el) =>
                    el &&
                    animateDropdown(
                      el,
                      openDropdowns.projectManagementLibrary && !isCollapsed,
                    )
                  }
                >
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${isPmTab("dashboard") ? "active" : ""}`}
                    to="/project-management?tab=dashboard"
                  >
                    <LayoutDashboard size={14} className="me-2 opacity-60" />{" "}
                    Dashboard
                  </Link>
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${isPmTab("projects") ? "active" : ""}`}
                    to="/project-management?tab=projects"
                  >
                    <BriefcaseBusiness size={14} className="me-2 opacity-60" />{" "}
                    Projects
                  </Link>
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${isPmTab("clients") ? "active" : ""}`}
                    to="/project-management?tab=clients"
                  >
                    <Building2 size={14} className="me-2 opacity-60" /> Clients
                  </Link>
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${isPmTab("members") ? "active" : ""}`}
                    to="/project-management?tab=members"
                  >
                    <UserSquare size={14} className="me-2 opacity-60" /> Project
                    Members
                  </Link>
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${isPmTab("kanban") ? "active" : ""}`}
                    to="/project-management?tab=kanban"
                  >
                    <KanbanSquare size={14} className="me-2 opacity-60" /> Kanban
                    Tasks
                  </Link>
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${isPmTab("tickets") ? "active" : ""}`}
                    to="/project-management?tab=tickets"
                  >
                    <Ticket size={14} className="me-2 opacity-60" /> Tickets
                  </Link>
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${isPmTab("templates") ? "active" : ""}`}
                    to="/project-management?tab=templates"
                  >
                    <FileTemplate size={14} className="me-2 opacity-60" /> Ticket
                    Templates
                  </Link>
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${isPmTab("activity") ? "active" : ""}`}
                    to="/project-management?tab=activity"
                  >
                    <Activity size={14} className="me-2 opacity-60" /> Activity
                    Logs
                  </Link>
                </div>
              </>
            )}
          </div>
        </li>
      )}

      <hr className="navbar-divider my-2" />

      {/* ── HRIS ── */}
      {canSeeHris && (
        <li
          className={`nav-item mb-1 dropdown ${openDropdowns.hris && !isCollapsed ? "show" : ""}`}
        >
          <a
            className={`nav-link dropdown-toggle rounded-3 ${isHrisActive() ? "active" : ""}`}
            href="#"
            onClick={(e) => toggleDropdown("hris", e)}
            role="button"
            aria-expanded={openDropdowns.hris && !isCollapsed}
          >
            <span className="nav-link-icon d-md-none d-lg-inline-block">
              <Users className="icon icon-tabler" />
            </span>
            <span className="nav-link-title">HRIS</span>
          </a>
          <div
            className={`dropdown-menu ${openDropdowns.hris && !isCollapsed ? "show" : ""}`}
            ref={(el) =>
              el && animateDropdown(el, openDropdowns.hris && !isCollapsed)
            }
          >
            <DropdownToggleItem
              dropdownKey="hrisRecruitment"
              icon={UserPlus}
              label="Recruitment Management"
              active={isPathInCategory(hrisRecruitmentPaths)}
            />
            <div
              className={`ps-3 ${openDropdowns.hrisRecruitment && !isCollapsed ? "" : "d-none"}`}
              ref={(el) =>
                el &&
                animateDropdown(
                  el,
                  openDropdowns.hrisRecruitment && !isCollapsed,
                )
              }
            >
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/recruitment" ? "active" : ""
                }`}
                to="/hris/recruitment"
              >
                <LayoutDashboard size={14} className="me-2 opacity-60" /> Dashboard
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/recruitment/manpower-request" ? "active" : ""
                }`}
                to="/hris/recruitment/manpower-request"
              >
                <ClipboardList size={14} className="me-2 opacity-60" /> Manpower Request
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/recruitment/job-requisition" ? "active" : ""
                }`}
                to="/hris/recruitment/job-requisition"
              >
                <BriefcaseBusiness size={14} className="me-2 opacity-60" /> Job Requisition
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/recruitment/job-vacancies" ? "active" : ""
                }`}
                to="/hris/recruitment/job-vacancies"
              >
                <ListChecks size={14} className="me-2 opacity-60" /> Job Vacancies
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/recruitment/candidates" ? "active" : ""
                }`}
                to="/hris/recruitment/candidates"
              >
                <Users size={14} className="me-2 opacity-60" /> Candidates
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/recruitment/selection-process" ? "active" : ""
                }`}
                to="/hris/recruitment/selection-process"
              >
                <ShieldCheck size={14} className="me-2 opacity-60" /> Selection Process
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/recruitment/job-offering" ? "active" : ""
                }`}
                to="/hris/recruitment/job-offering"
              >
                <FileText size={14} className="me-2 opacity-60" /> Job Offering
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/recruitment/hiring-approval" ? "active" : ""
                }`}
                to="/hris/recruitment/hiring-approval"
              >
                <Award size={14} className="me-2 opacity-60" /> Hiring Approval
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/recruitment/pre-onboarding" ? "active" : ""
                }`}
                to="/hris/recruitment/pre-onboarding"
              >
                <UserSquare size={14} className="me-2 opacity-60" /> Pre-Onboarding
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/recruitment/reports" ? "active" : ""
                }`}
                to="/hris/recruitment/reports"
              >
                <BarChart3 size={14} className="me-2 opacity-60" /> Recruitment Reports
              </Link>
            </div>

            <DropdownToggleItem
              dropdownKey="hrisEmployee"
              icon={Users}
              label="Employee Management"
              active={
                isPathInCategory(hrisEmployeePaths) ||
                currentPath.startsWith("/hris/employees/")
              }
            />
            <div
              className={`ps-3 ${openDropdowns.hrisEmployee && !isCollapsed ? "" : "d-none"}`}
              ref={(el) =>
                el &&
                animateDropdown(el, openDropdowns.hrisEmployee && !isCollapsed)
              }
            >
              {canViewModule("hris") && (
                <Link
                  className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                    currentPath === "/hris/dashboard" ? "active" : ""
                  }`}
                  to="/hris/dashboard"
                >
                  <LayoutDashboard size={14} className="me-2 opacity-60" /> Dashboard
                </Link>
              )}
              {(canViewModule("hris") || canViewModule("employee")) && (
                <Link
                  className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                    currentPath === "/hris/employees" ||
                    currentPath.startsWith("/hris/employees/")
                      ? "active"
                      : ""
                  }`}
                  to="/hris/employees"
                >
                  <UserCircle size={14} className="me-2 opacity-60" /> Employees
                </Link>
              )}

              <DropdownToggleItem
                dropdownKey="hrisOrganization"
                icon={Building2}
                label="Organization"
                active={isPathInCategory(hrisOrganizationPaths)}
              />
              <div
                className={`ps-3 ${openDropdowns.hrisOrganization && !isCollapsed ? "" : "d-none"}`}
                ref={(el) =>
                  el &&
                  animateDropdown(
                    el,
                    openDropdowns.hrisOrganization && !isCollapsed,
                  )
                }
              >
                {(canViewModule("hris") || canViewModule("department")) && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 d-flex align-items-center ${
                      currentPath === "/hris/department" ? "active" : ""
                    }`}
                    to="/hris/department"
                  >
                    <Settings size={14} className="me-2 opacity-60" /> Departments
                  </Link>
                )}
                <Link
                  className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                    currentPath === "/hris/job-title" ? "active" : ""
                  }`}
                  to="/hris/job-title"
                >
                  <BriefcaseBusiness size={14} className="me-2 opacity-60" /> Job Titles
                </Link>
                <Link
                  className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                    currentPath === "/hris/grades" ? "active" : ""
                  }`}
                  to="/hris/grades"
                >
                  <Award size={14} className="me-2 opacity-60" /> Grades
                </Link>
                <Link
                  className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                    currentPath === "/hris/organization-structure" ? "active" : ""
                  }`}
                  to="/hris/organization-structure"
                >
                  <PieChart size={14} className="me-2 opacity-60" /> Organization Structure
                </Link>
              </div>

              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/branch" ? "active" : ""
                }`}
                to="/hris/branch"
              >
                <Building2 size={14} className="me-2 opacity-60" /> Branches
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/competencies" ? "active" : ""
                }`}
                to="/hris/competencies"
              >
                <Award size={14} className="me-2 opacity-60" /> Competencies
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/employee-assignments" ? "active" : ""
                }`}
                to="/hris/employee-assignments"
              >
                <UserSquare size={14} className="me-2 opacity-60" /> Employee Assignments
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/employment-administration" ? "active" : ""
                }`}
                to="/hris/employment-administration"
              >
                <FileText size={14} className="me-2 opacity-60" /> Employment Administration
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/employee-documents" ? "active" : ""
                }`}
                to="/hris/employee-documents"
              >
                <Archive size={14} className="me-2 opacity-60" /> Employee Documents
              </Link>
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/employee-reports" ? "active" : ""
                }`}
                to="/hris/employee-reports"
              >
                <BarChart3 size={14} className="me-2 opacity-60" /> Employee Reports
              </Link>
            </div>

            <DropdownToggleItem
              dropdownKey="hrisPerformance"
              icon={Target}
              label="Performance Management"
              active={isPathInCategory(hrisPerformancePaths)}
            />
            <div
              className={`ps-3 ${openDropdowns.hrisPerformance && !isCollapsed ? "" : "d-none"}`}
              ref={(el) =>
                el &&
                animateDropdown(
                  el,
                  openDropdowns.hrisPerformance && !isCollapsed,
                )
              }
            >
              <Link
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${
                  currentPath === "/hris/performance" ? "active" : ""
                }`}
                to="/hris/performance"
              >
                <LayoutDashboard size={14} className="me-2 opacity-60" /> Dashboard
              </Link>
              <ComingSoonItem icon={CalendarDays} label="Performance Period" />
              <ComingSoonItem icon={Target} label="KPI & Goals" />
              <ComingSoonItem icon={Award} label="Competency Assessment" />
              <ComingSoonItem icon={ListChecks} label="Performance Review" />
              <ComingSoonItem icon={ShieldCheck} label="Calibration" />
              <ComingSoonItem icon={ClipboardList} label="Development Plan" />
              <ComingSoonItem icon={FileText} label="Performance Improvement Plan" />
              <ComingSoonItem icon={Activity} label="Performance History" />
              <ComingSoonItem icon={BarChart3} label="Performance Reports" />
            </div>
          </div>
        </li>
      )}

      {/* ── COMING SOON ITEMS ── */}
      {[
        {
          label: "Academy",
          icon: <GraduationCap className="icon icon-tabler" />,
        },
        {
          label: "Knowledge Management",
          icon: <Database className="icon icon-tabler" />,
        },
        {
          label: "Document Management",
          icon: <FolderOpen className="icon icon-tabler" />,
        },
        {
          label: "Asset Management",
          icon: <Package className="icon icon-tabler" />,
        },
      ].map(({ label, icon }) => (
        <li
          key={label}
          className="nav-item mb-1"
          style={{ opacity: 0.5, cursor: "default" }}
        >
          <span
            className="nav-link rounded-3 d-flex align-items-center"
            style={{ cursor: "default" }}
          >
            <span className="nav-link-icon d-md-none d-lg-inline-block">
              {icon}
            </span>
            <span className="nav-link-title flex-grow-1">{label}</span>
            <ComingSoonBadge />
          </span>
        </li>
      ))}

      {/* ── SETTINGS ── */}
      {canSeeSettings && (
        <>
          <hr className="navbar-divider my-2" />
          <li
            className={`nav-item mb-1 dropdown ${openDropdowns.settings && !isCollapsed ? "show" : ""}`}
          >
            <a
              className={`nav-link dropdown-toggle rounded-3 ${isSettingsActive() ? "active" : ""}`}
              href="#"
              onClick={(e) => toggleDropdown("settings", e)}
              role="button"
              aria-expanded={openDropdowns.settings && !isCollapsed}
            >
              <span className="nav-link-icon d-md-none d-lg-inline-block">
                <Settings className="icon icon-tabler" />
              </span>
              <span className="nav-link-title">Settings</span>
            </a>
            <div
              className={`dropdown-menu ${openDropdowns.settings && !isCollapsed ? "show" : ""}`}
              ref={(el) =>
                el &&
                animateDropdown(el, openDropdowns.settings && !isCollapsed)
              }
            >
              {canSeeSettingsTools && (
                <>
              {/* Tools sub-menu */}
              <a
                className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${isPathInCategory(settingsToolsPaths) ? "active" : ""}`}
                href="#"
                onClick={(e) => toggleDropdown("settingsTools", e)}
              >
                <Wrench size={14} className="me-2 opacity-60" />
                <span className="flex-grow-1">Tools</span>
                <ChevronDown
                  size={12}
                  style={{
                    transform: openDropdowns.settingsTools
                      ? "rotate(180deg)"
                      : "none",
                    transition: "transform 0.2s",
                  }}
                />
              </a>
              <div
                className={`ps-3 ${openDropdowns.settingsTools && !isCollapsed ? "" : "d-none"}`}
                ref={(el) =>
                  el &&
                  animateDropdown(
                    el,
                    openDropdowns.settingsTools && !isCollapsed,
                  )
                }
              >
                {canViewModule("create-template") && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/create-template" ? "active" : ""}`}
                    to="/create-template"
                  >
                    Proposal Template
                  </Link>
                )}
                {canViewModule("workflow") && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/approval-workflow" ? "active" : ""}`}
                    to="/approval-workflow"
                  >
                    Approval Workflow
                  </Link>
                )}
                {canViewModule("rate-management") && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/rate-management" ? "active" : ""}`}
                    to="/rate-management"
                  >
                    Rate Management
                  </Link>
                )}
                {canViewModule("create-type-unit") && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/create-type-unit" ? "active" : ""}`}
                    to="/create-type-unit"
                  >
                    Type &amp; Unit
                  </Link>
                )}
                {canViewModule("proposal-stage") && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/proposal-stage" ? "active" : ""}`}
                    to="/proposal-stage"
                  >
                    Proposal Stage
                  </Link>
                )}
                {canViewModule("format-number") && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/format-number" ? "active" : ""}`}
                    to="/format-number"
                  >
                    Number Format
                  </Link>
                )}
                {canViewModule("expense-category") && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/expense-category" ? "active" : ""}`}
                    to="/expense-category"
                  >
                    Expense Category
                  </Link>
                )}
                {canViewModule("chart-of-account") && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/chart-of-account" ? "active" : ""}`}
                    to="/chart-of-account"
                  >
                    Chart of Account
                  </Link>
                )}
                {canViewModule("master-currency") && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/master-currency" ? "active" : ""}`}
                    to="/master-currency"
                  >
                    Currency
                  </Link>
                )}
                {canViewModule("sales-target") && (
                  <Link
                    className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/sales-target" ? "active" : ""}`}
                    to="/sales-target"
                  >
                    <Target size={14} className="me-2 opacity-60" /> Sales
                    Target Management
                  </Link>
                )}
              </div>

                </>
              )}

              {canSeeSettingsRole && (
                <>
                  {/* Role & Permissions sub-menu */}
                  <a
                    className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${isPathInCategory(settingsRolePaths) ? "active" : ""}`}
                    href="#"
                    onClick={(e) => toggleDropdown("settingsRole", e)}
                  >
                    <ShieldCheck size={14} className="me-2 opacity-60" />
                    <span className="flex-grow-1">Role &amp; Permissions</span>
                    <ChevronDown
                      size={12}
                      style={{
                        transform: openDropdowns.settingsRole
                          ? "rotate(180deg)"
                          : "none",
                        transition: "transform 0.2s",
                      }}
                    />
                  </a>
                  <div
                    className={`ps-3 ${openDropdowns.settingsRole && !isCollapsed ? "" : "d-none"}`}
                    ref={(el) =>
                      el &&
                      animateDropdown(
                        el,
                        openDropdowns.settingsRole && !isCollapsed,
                      )
                    }
                  >
                    {canViewModule("create-role") && (
                      <Link
                        className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/create-role" ? "active" : ""}`}
                        to="/create-role"
                      >
                        Roles
                      </Link>
                    )}
                    {canViewModule("set-permission") && (
                      <Link
                        className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/set-permission" ? "active" : ""}`}
                        to="/set-permission"
                      >
                        Permissions
                      </Link>
                    )}
                  </div>

                </>
              )}

              {canSeeSettingsEmail && (
                <>
                  {/* Email Settings sub-menu */}
                  <a
                    className={`dropdown-item rounded-3 mb-1 mt-1 d-flex align-items-center ${isPathInCategory(settingsEmailPaths) ? "active" : ""}`}
                    href="#"
                    onClick={(e) => toggleDropdown("settingsEmail", e)}
                  >
                    <Mail size={14} className="me-2 opacity-60" />
                    <span className="flex-grow-1">Email Settings</span>
                    <ChevronDown
                      size={12}
                      style={{
                        transform: openDropdowns.settingsEmail
                          ? "rotate(180deg)"
                          : "none",
                        transition: "transform 0.2s",
                      }}
                    />
                  </a>
                  <div
                    className={`ps-3 ${openDropdowns.settingsEmail && !isCollapsed ? "" : "d-none"}`}
                    ref={(el) =>
                      el &&
                      animateDropdown(
                        el,
                        openDropdowns.settingsEmail && !isCollapsed,
                      )
                    }
                  >
                    {canViewModule("email-template") && (
                      <Link
                        className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/email-template" ? "active" : ""}`}
                        to="/email-template"
                      >
                        Email Template
                      </Link>
                    )}
                    {canViewModule("email-configuration") && (
                      <Link
                        className={`dropdown-item rounded-3 mb-1 mt-1 ${currentPath === "/email-configuration" ? "active" : ""}`}
                        to="/email-configuration"
                      >
                        Email Configuration
                      </Link>
                    )}
                  </div>
                </>
              )}
            </div>
          </li>
        </>
      )}
    </ul>
  );
};

export default NavLinks;
